import { supabase } from "@/integrations/supabase/client";
import type { Stem } from '@/types/stem';
import { calculateProcessingTime } from '@/utils/audioUtils';
import { setSessionIdHeader } from '@/api/sessionApi';

export const getProcessingDuration = async (audioUrl: string): Promise<number> => {
  return new Promise((resolve) => {
    const audio = new Audio(audioUrl);
    audio.addEventListener('loadedmetadata', () => {
      const estimatedProcessingTime = calculateProcessingTime(audio.duration);
      resolve(estimatedProcessingTime);
    });
  });
};

export const uploadFile = async (file: File): Promise<string> => {
  // Ensure session is set before uploading
  await setSessionIdHeader();
  
  const timestamp = new Date().getTime();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').toLowerCase();
  const filePath = `${timestamp}-${sanitizedName}`;
  
  console.log('Uploading file to Supabase storage:', filePath);
  const { error: uploadError } = await supabase.storage
    .from('audio-files')
    .upload(filePath, file);

  if (uploadError) {
    console.error('Upload error:', uploadError);
    throw uploadError;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('audio-files')
    .getPublicUrl(filePath);

  console.log('File uploaded successfully, public URL:', publicUrl);
  return publicUrl;
};

export const processStemSplit = async (
  audioUrl: string, 
  mode: 'aggregate-6stem-v2' | 'aggregate-vocals' | 'aggregate-drumsep' = 'aggregate-6stem-v2'
): Promise<Stem[]> => {
  // Ensure session is set before making the function call
  const sessionId = await setSessionIdHeader();
  console.log('Processing stems with session ID:', sessionId);
  console.log('Using mode:', mode);
  
  // Set a timeout of 5 minutes
  const timeoutDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('Processing timeout: The operation took too long to complete'));
    }, timeoutDuration);
  });

  try {
    // Race between the processing and the timeout
    const { data, error } = await Promise.race([
      supabase.functions.invoke('process-stems', {
        body: { audioUrl, mode }
      }),
      timeoutPromise
    ]) as { data: any, error: any };

    if (error) {
      console.error('Error from process-stems function:', error);
      throw error;
    }

    if (!data?.result || !Array.isArray(data.result)) {
      console.error('Invalid response from process-stems:', data);
      throw new Error('Invalid response from stem processing service');
    }

    // Get audio duration
    const audio = new Audio(audioUrl);
    await new Promise((resolve) => {
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
    });
    const duration = audio.duration;

    console.log('Received stem processing result:', data.result);

    // Map the new API response to the Stem[] format
    const stems: Stem[] = data.result.map((item: any) => ({
      name: item.instrument.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase()), // Format instrument name
      url: item.public_url,
      duration
    }));

    // Prepend the "Original" stem
    stems.unshift({
      name: 'Original',
      url: audioUrl,
      duration
    });

    // Remove any duplicates by URL
    const uniqueStems = stems.filter((stem, index, self) => 
      index === self.findIndex((s) => s.url === stem.url)
    );

    // Verify we have at least two stems (original + at least one other)
    if (uniqueStems.length < 2) {
      console.error('Missing stems in result:', data.result);
      throw new Error('Processing service did not return the required stems');
    }

    return uniqueStems;
  } catch (error) {
    console.error('Error in processStemSplit:', error);
    if (error.message.includes('timeout')) {
      throw new Error('The stem processing took too long to complete. Please try again or use a shorter audio file.');
    }
    throw error;
  }
};
